import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export interface TextMatchResult {
  from: number;
  to: number;
}

const SECTION_HEADER_PATTERN =
  /^(hpi|history of present(ing)? illness|subjective|ros|review of systems|pe|physical exam(ination)?|objective|assessment\s*[&+]\s*plan|a\s*[&+]\s*p|assessment|plan)\s*:?\s*$/i;

function getSectionNodeLabel(node: ProseMirrorNode): string {
  if (node.type.name !== 'sectionNode') return '';

  const attrs = node.attrs as Record<string, unknown>;
  return [attrs.sectionTitle, attrs.linkedEhrSectionId]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
}

function isMatchingSectionNode(node: ProseMirrorNode, lowerHeading: string): boolean {
  const label = getSectionNodeLabel(node);
  return label.length > 0 && label.includes(lowerHeading);
}

/**
 * Returns true when a ProseMirror node looks like a clinical note section header
 * rather than A&P content (numbered items, bullet points, blank lines).
 */
function isSectionHeader(node: ProseMirrorNode, lowerText: string): boolean {
  if (node.type.name === 'heading') return true;
  if (node.type.name !== 'paragraph') return false;
  if (lowerText.length === 0) return false;
  if (lowerText.length > 60) return false;
  if (/^\d+[\.\)]/.test(lowerText)) return false;
  if (/^[-–—•*]/.test(lowerText)) return false;
  const hasBold =
    node.content.size > 0 &&
    (() => {
      let bold = false;
      node.descendants((child) => {
        if (child.marks?.some((m) => m.type.name === 'bold')) bold = true;
      });
      return bold;
    })();
  if (hasBold) return true;
  if (SECTION_HEADER_PATTERN.test(lowerText)) return true;
  if (lowerText.endsWith(':') && lowerText.length <= 40) return true;
  return false;
}

/**
 * Find the first occurrence of `needle` in the ProseMirror document.
 *
 * Scans all text nodes, building a contiguous text buffer per block,
 * then searches for the needle within that buffer. Returns absolute
 * document positions suitable for Decoration.inline(from, to, ...).
 *
 * When `sectionHeading` is provided, the search is scoped to the first
 * block-level section whose heading text contains the value (case-insensitive).
 * This avoids false positives when the same phrase appears in multiple sections.
 *
 * Returns null if the text cannot be found (e.g. user edited it away).
 */
export function findTextInDoc(
  doc: ProseMirrorNode,
  needle: string,
  sectionHeading?: string | null
): TextMatchResult | null {
  if (!needle.trim()) return null;

  const normalised = needle.replace(/\s+/g, ' ').trim();

  let result: TextMatchResult | null;
  if (sectionHeading) {
    result = searchDoc(doc, normalised, sectionHeading);
  } else {
    result = searchDoc(doc, normalised, null);
  }

  return result;
}

export interface SectionRange {
  from: number;
  to: number;
}

/**
 * Find the document position range for a named section.
 * Returns the pos of the section heading through the pos of the next
 * heading/short-paragraph that deactivates the section, or doc end.
 */
export function findSectionRange(
  doc: ProseMirrorNode,
  sectionHeading: string
): SectionRange | null {
  let from: number | null = null;
  let to: number | null = null;
  const lower = sectionHeading.toLowerCase();

  doc.descendants((node, pos) => {
    if (to !== null) return false;

    if (node.isBlock) {
      if (from === null && isMatchingSectionNode(node, lower)) {
        from = pos;
        to = pos + node.nodeSize;
        return false;
      }

      const text = node.textContent.trim().toLowerCase();
      const isHeader = isSectionHeader(node, text);

      if (from === null) {
        if (isHeader && text.includes(lower)) {
          from = pos;
          return true;
        }
      } else {
        if (isHeader && !text.includes(lower)) {
          to = pos;
          return false;
        }
      }
    }
    return true;
  });

  if (from === null) return null;
  return { from, to: to ?? doc.content.size };
}

/**
 * Walk the ProseMirror document tree looking for `normalised` text.
 *
 * When `sectionHeading` is non-null the search is constrained to the
 * section whose header contains that string (toggles on/off as headers
 * are encountered). Within the active section it scans each textblock,
 * normalises whitespace, and maps the plain-text offset back to absolute
 * document positions across potentially fragmented text nodes.
 *
 * @param doc - The ProseMirror document to search.
 * @param normalised - Whitespace-normalised needle string.
 * @param sectionHeading - If non-null, restricts the search to the section
 *   whose header text includes this value (case-insensitive).
 * @returns Absolute `{from, to}` positions, or `null` if not found.
 */
function searchDoc(
  doc: ProseMirrorNode,
  normalised: string,
  sectionHeading: string | null
): TextMatchResult | null {
  let sectionActive = !sectionHeading;
  let result: TextMatchResult | null = null;

  doc.descendants((node, pos) => {
    if (result) return false;

    // Activate/deactivate section at recognized section headers
    if (sectionHeading && node.isBlock) {
      if (node.type.name === 'sectionNode') {
        sectionActive = isMatchingSectionNode(node, sectionHeading.toLowerCase());
        return sectionActive;
      }

      const text = node.textContent.trim().toLowerCase();
      if (isSectionHeader(node, text)) {
        sectionActive = text.includes(sectionHeading.toLowerCase());
        if (sectionActive) return true;
        return false;
      }
    }

    if (!sectionActive) return true;

    if (node.isTextblock) {
      const blockText = node.textContent;
      const normalisedBlock = blockText.replace(/\s+/g, ' ');
      const idx = normalisedBlock.toLowerCase().indexOf(normalised.toLowerCase());
      if (idx === -1) return false;

      const normToOrig = buildNormToOrigMapping(blockText);
      const origStartIdx = normToOrig[idx];
      const origEndIdx = normToOrig[idx + normalised.length];

      if (origStartIdx === undefined || origEndIdx === undefined) return false;

      let charCount = 0;
      let startPos: number | null = null;
      let endPos: number | null = null;

      node.descendants((child, childOffset) => {
        if (startPos !== null && endPos !== null) return false;
        if (!child.isText || !child.text) return false;

        const text = child.text;
        for (let i = 0; i < text.length; i++) {
          if (charCount === origStartIdx && startPos === null) {
            startPos = pos + 1 + childOffset + i;
          }
          if (charCount === origEndIdx) {
            endPos = pos + 1 + childOffset + i;
            return false;
          }
          charCount++;
        }
        if (charCount === origEndIdx && endPos === null) {
          endPos = pos + 1 + childOffset + text.length;
        }
        return false;
      });

      if (startPos !== null && endPos !== null) {
        result = { from: startPos, to: endPos };
      }
      return false;
    }

    return true;
  });

  return result;
}

/**
 * Build a mapping from normalized string positions to original string positions.
 * Whitespace runs are collapsed to single spaces in the normalized string.
 */
export function buildNormToOrigMapping(blockText: string): number[] {
  const normToOrig: number[] = [];
  let prevWasSpace = false;

  for (let origIdx = 0; origIdx < blockText.length; origIdx++) {
    const isSpace = /\s/.test(blockText[origIdx]!);

    if (isSpace) {
      if (!prevWasSpace) {
        normToOrig.push(origIdx);
      }
      prevWasSpace = true;
    } else {
      normToOrig.push(origIdx);
      prevWasSpace = false;
    }
  }
  normToOrig.push(blockText.length);
  return normToOrig;
}

/**
 * Find the first occurrence of `needle` within a specific position range.
 *
 * Unlike `findTextInDoc`, this does not rely on section headers; it searches
 * only within the `[rangeStart, rangeEnd)` document positions. Use this when
 * you already know the block boundaries (e.g. problem block in the A&P) and
 * want to avoid false positives from other parts of the document.
 */
export function findTextInRange(
  doc: ProseMirrorNode,
  needle: string,
  rangeStart: number,
  rangeEnd: number
): TextMatchResult | null {
  const normalised = needle.replace(/\s+/g, ' ').trim();
  if (!normalised) return null;

  let result: TextMatchResult | null = null;

  doc.nodesBetween(rangeStart, rangeEnd, (node, pos) => {
    if (result) return false;
    if (!node.isTextblock) return true;

    const blockText = node.textContent;
    const normalisedBlock = blockText.replace(/\s+/g, ' ');
    const idx = normalisedBlock.toLowerCase().indexOf(normalised.toLowerCase());
    if (idx === -1) return false;

    const normToOrig = buildNormToOrigMapping(blockText);
    const origStartIdx = normToOrig[idx];
    const origEndIdx = normToOrig[idx + normalised.length];

    if (origStartIdx === undefined || origEndIdx === undefined) return false;

    let charCount = 0;
    let startPos: number | null = null;
    let endPos: number | null = null;

    node.descendants((child, childOffset) => {
      if (startPos !== null && endPos !== null) return false;
      if (!child.isText || !child.text) return false;

      const text = child.text;
      for (let i = 0; i < text.length; i++) {
        if (charCount === origStartIdx && startPos === null) {
          startPos = pos + 1 + childOffset + i;
        }
        if (charCount === origEndIdx) {
          endPos = pos + 1 + childOffset + i;
          return false;
        }
        charCount++;
      }
      if (charCount === origEndIdx && endPos === null) {
        endPos = pos + 1 + childOffset + text.length;
      }
      return false;
    });

    if (startPos !== null && endPos !== null) {
      result = { from: startPos, to: endPos };
    }
    return false;
  });

  return result;
}
